import { maxBy, orderBy } from 'lodash-es'

import { isFedex } from '@fv/client-core'
import type { FullShipment, Quote, ShipmentLocation } from '@fv/client-types'
import { fvDate } from '@fv/dates'
import type { ApiChargeDTO } from '@fv/models'
import { dates } from '@fv/models/core'

import { locationToFormStop } from '@/utils/locationFuncs'
import { isProcessingQuotes } from '@/utils/shipmentFuncs'

import { equipmentToFormLoad } from '../quote-request/quoteRequestUtils'
import type { QuoteRequestLoads } from '../quote-request/types'
import type { QuotePageLoadDetails } from './types'

export type ImportantQuoteProps = Pick<
  Quote,
  | '_id'
  | 'pricingType'
  | 'serviceId'
  | 'method'
  | 'pricingMethod'
  | 'carrierId'
  | 'mode'
  | 'status'
  | 'isSpot'
>

export const quoteFuncs = {
  isSpotWorkflow(quote?: ImportantQuoteProps) {
    if (!quote) return false

    return !!quote.isSpot
  },

  getRecentAPIQuoteDate(
    { createdDate }: Pick<FullShipment, 'createdDate'>,
    quotes?: Quote[],
  ) {
    const apiQuotes = quotes?.filter(r => r.method === 'api') ?? []
    const quoteDate = maxBy(apiQuotes, r => r.createdDate)?.createdDate

    return quoteDate ?? createdDate
  },

  isFedex(quote?: Quote) {
    return isFedex(quote?.providerCode ?? quote?.assetCarrierCode)
  },

  filterActiveDirect(quotes?: Quote[]) {
    return (
      quotes?.filter(
        q =>
          !quoteFuncs.isSpotWorkflow(q) &&
          (q.status === 'active' || q.status === 'awarded'),
      ) ?? []
    )
  },

  cheapestQuote(quotes?: Quote[]) {
    return orderBy(quotes ?? [], q => q.amount)[0]
  },

  getEstimatedDeliveryDate(
    pickupDate?: string,
    quoteProps?: Pick<Quote, 'transitDaysMin' | 'deliveryDate'>,
  ) {
    return dates.getEstimatedDeliveryDate(pickupDate, quoteProps)
  },
  sortCharges: (charges: Array<ApiChargeDTO>) => {
    if (!charges) return []

    return charges.slice().sort((a, b) => {
      if (a.name === 'linehaul') return -1
      if (b.name === 'linehaul') return 1
      if (a.name === 'base rate') return -1
      if (b.name === 'base rate') return 1
      if (a.name === 'discount') return -1
      if (b.name === 'discount') return 1
      if (a.name === 'fuel') return -1
      if (b.name === 'fuel') return 1
      return 0
    })
  },
  carrierSort: (q: Quote) =>
    (q.assetCarrierName ?? q.providerName).trim().toLowerCase(),
  guaranteedSort: (q: Quote) => q.guaranteed || /guaranteed/i.test(q.serviceId),
  interlineSort: (q: Quote) => !!q.interline,
  isPendingQuote: (
    quote: Quote,
    shipment: Pick<FullShipment, 'quoteProcessStatus'>,
  ) =>
    quote.method === 'api' &&
    quote.status === 'active' &&
    !quote.amount &&
    isProcessingQuotes(shipment),
}

function getQuoteRequestIdentifier(loads?: FullShipment[]) {
  if (!loads?.length) return ''

  const refNums = new Set<string>()

  loads.forEach(s => {
    s.refNums?.forEach(n => {
      refNums.add(n.value)
    })
  })

  if (refNums.size > 0) return Array.from(refNums).join(', ')

  const stops = loads[0].locations
  const origin = stops[0]
  const dest = stops[stops.length - 1]

  return `${origin.city}, ${origin.state} to ${dest.city}, ${dest.state}`
}

export type QuoteRequestMode = 'copy' | 'edit'

/** A repeated shipment keeps its stops but drops ref numbers, appointment dates, and a pickup date that has passed. */
export const copyShipmentLocations = (
  locations: ShipmentLocation[],
): ShipmentLocation[] => {
  const isAfterPickUp = fvDate().isAfter(fvDate(locations[0]?.stopDate), 'day')

  return locations.map(
    (x): ShipmentLocation => ({
      ...x,
      refNums: [],
      accessorials: x.accessorials?.map(({ appointmentDate, ...a }) => a),
      ...(isAfterPickUp && {
        stopDate: x.sequence === 0 ? fvDate().toISOString() : null,
      }),
    }),
  )
}

export const quoteRequestMapper = {
  ltlRequest: (props: {
    load: FullShipment
    mode: QuoteRequestMode
  }): Omit<QuotePageLoadDetails, 'items'> => {
    const { load, mode } = props
    const locations =
      mode === 'copy' ? copyShipmentLocations(load.locations) : load.locations

    return {
      load: {
        isFreightCollect: !!load.isFreightCollect,
        loadId: load.loadId,
        workflow: load.workflow,
        protectFromFreezing: !!load.equipment.accessorials?.some(
          a => a.key === 'protect-from-freezing',
        ),
        singleShipment: !!load.equipment.accessorials?.some(
          a => a.key === 'single-shipment',
        ),
        quoteRequestId: load.quoteRequestId,
        rateLocationType:
          load.direction === 'inbound' ? 'destination' : 'origin',
        schedulePickup: load.schedulePickup,
      },
      locations: locations.map(x => locationToFormStop(x, load.workflow)),
    }
  },
  tlRequest: (templates: FullShipment[], mode: QuoteRequestMode) => {
    const firstLoad = templates[0]
    const loads: QuoteRequestLoads = new Map()
    const trimRefNums = mode === 'copy'
    const orderNum = firstLoad.refNums?.[0]?.value ?? ''
    const metadata = {
      isLiveLoad: firstLoad.isLiveLoad,
      orderNumber: trimRefNums ? '' : orderNum,
      quoteRequestId: firstLoad.quoteRequestId,
      requestIdentifier: getQuoteRequestIdentifier(templates),
    }

    templates.forEach(({ equipment, loadId, items }) => {
      const nextLoadsOfType = loads.get(equipment.type) ?? []
      const nextLoad = equipmentToFormLoad({
        equipment,
        loadId,
        items,
        billTo: firstLoad.billTo,
        customsBroker: firstLoad.customsBroker,
        emergencyContact: firstLoad.emergencyContact,
      })

      loads.set(equipment.type, nextLoadsOfType.concat(nextLoad))
    })

    // Activate first of each equipment type
    for (const type of loads.keys()) {
      const firstLoadOfType = loads.get(type)?.[0]
      if (firstLoadOfType) firstLoadOfType.isActiveInUI = true
    }

    const stops = (
      mode === 'copy'
        ? copyShipmentLocations(firstLoad.locations)
        : firstLoad.locations
    ).map(x => locationToFormStop(x, 'truckload'))

    return { loads, metadata, stops }
  },
}
